import type { FieldErrors, FieldValues } from 'react-hook-form';

export interface ValidationSummaryItem {
  field: string;
  message?: string;
}

const METADATA_KEYS = new Set(['message', 'type', 'types', 'ref']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isNumericSegment = (segment: string): boolean => /^\d+$/.test(segment);

const appendPath = (currentPath: string, segment: string): string => {
  if (segment === 'root') {
    return currentPath;
  }

  if (!currentPath) {
    return isNumericSegment(segment) ? `[${segment}]` : segment;
  }

  return isNumericSegment(segment) ? `${currentPath}[${segment}]` : `${currentPath}.${segment}`;
};

const normalizePath = (path: string): string => path.replace(/\.root$/, '').trim();

const collectValidationSummary = (
  node: unknown,
  currentPath: string,
  output: ValidationSummaryItem[]
): void => {
  if (!node) {
    return;
  }

  if (Array.isArray(node)) {
    node.forEach((item, index) => {
      collectValidationSummary(item, appendPath(currentPath, String(index)), output);
    });
    return;
  }

  if (!isRecord(node)) {
    return;
  }

  const message = typeof node.message === 'string' ? node.message : undefined;
  if (message && currentPath) {
    output.push({
      field: normalizePath(currentPath),
      message,
    });
  }

  Object.entries(node).forEach(([key, value]) => {
    if (METADATA_KEYS.has(key)) {
      return;
    }

    collectValidationSummary(value, appendPath(currentPath, key), output);
  });
};

export const buildValidationSummary = <TFieldValues extends FieldValues>(
  errors: FieldErrors<TFieldValues>
): ValidationSummaryItem[] => {
  const collected: ValidationSummaryItem[] = [];
  collectValidationSummary(errors as unknown, '', collected);

  if (collected.length === 0) {
    return Object.keys(errors).map((field) => ({ field }));
  }

  const deduplicated = new Map<string, ValidationSummaryItem>();
  collected.forEach((item) => {
    const key = `${item.field}|${item.message ?? ''}`;
    if (!deduplicated.has(key)) {
      deduplicated.set(key, item);
    }
  });

  return Array.from(deduplicated.values());
};
